import { useEffect, useRef } from "react";

/**
 * useSpatialNav — Smart-TV arrow-key navigation.
 *
 * On mount, attaches a keydown listener to the window. Arrow keys move focus
 * between elements marked `data-tv-focus="true"`. Enter/Space activate them.
 * Back/Escape calls the `onBack` callback.
 *
 * Focus order is determined by the element's getBoundingClientRect, so this
 * works with any layout (grids, rails, lists) without wiring up explicit
 * up/down/left/right refs — the closest-candidate in the arrow direction wins.
 *
 * Also enables the Wake Lock API (if supported) so idle screensavers don't
 * kick in during playback.
 */
export function useSpatialNav(onBack) {
  useEffect(() => {
    // Auto-focus the first focusable element so the very first arrow / Tab
    // press has somewhere to start. Without this, freshly-mounted TV pages
    // ignore the first remote press because document.activeElement is the
    // <body>. We retry a few times so async-rendered cards (rails fetched
    // from the API) get the focus once they land.
    const tryAutoFocus = (attempt = 0) => {
      if (document.activeElement && document.activeElement !== document.body) return;
      const first = document.querySelector('[data-tv-focus="true"]:not([disabled])');
      if (first) {
        try { first.focus({ preventScroll: false }); } catch { /* ignore */ }
      } else if (attempt < 12) {
        setTimeout(() => tryAutoFocus(attempt + 1), 250);
      }
    };
    tryAutoFocus();

    const onKey = (e) => {
      const active = document.activeElement;
      const key = e.key;
      // Some old Tizen / webOS / Fire TV remotes report numeric keyCodes
      // instead of 'ArrowUp'. Translate the common ones.
      const keyCodeMap = {
        37: "ArrowLeft", 38: "ArrowUp", 39: "ArrowRight", 40: "ArrowDown",
        13: "Enter",     27: "Escape",   8: "Backspace",   9: "Tab",
        // Tizen / webOS / Android TV media buttons
        461: "Escape",   166: "Escape",  // KEY_BACK on some TVs
      };
      const effectiveKey = (key && key !== "Unidentified") ? key : (keyCodeMap[e.keyCode] || key);

      // Back / Escape
      if (effectiveKey === "Escape" || effectiveKey === "Backspace" || effectiveKey === "GoBack") {
        // Don't intercept backspace inside inputs
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA") && effectiveKey === "Backspace") return;
        if (onBack) { e.preventDefault(); onBack(); }
        return;
      }

      // Enter / Space activate the focused control (native default handles it)
      if (effectiveKey === "Enter" || effectiveKey === " ") return;

      // Tab cycle — drive the same focus engine but in DOM order so the
      // user can ALSO surf with Tab / Shift+Tab (matches TV browsers and
      // wireless keyboards plugged into a Smart TV). Wraps to the start
      // / end so the user never gets "stuck" off-screen.
      if (effectiveKey === "Tab") {
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) return;
        e.preventDefault();
        const focusables = Array.from(document.querySelectorAll('[data-tv-focus="true"]:not([disabled])'))
          .filter((el) => el.offsetParent !== null);
        if (focusables.length === 0) return;
        const idx = focusables.indexOf(active);
        const dir = e.shiftKey ? -1 : 1;
        const next = focusables[(idx + dir + focusables.length) % focusables.length] || focusables[0];
        next.focus({ preventScroll: false });
        next.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        return;
      }

      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "MediaChannelUp", "MediaChannelDown", "ChannelUp", "ChannelDown"].includes(effectiveKey)) return;

      // If the focused element is an input or scrollable text area, let the default handle it
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) return;

      // Page / Channel keys — always scroll the page regardless of focus graph.
      if (effectiveKey === "PageDown" || effectiveKey === "PageUp" || effectiveKey === "MediaChannelUp" || effectiveKey === "MediaChannelDown" || effectiveKey === "ChannelUp" || effectiveKey === "ChannelDown") {
        e.preventDefault();
        // Prefer the scroll container the user's focus is sitting in. If
        // nothing is focused yet (Smart-TV browsers often park focus on
        // <body> after a route change), explicitly target the TV app's
        // own scroll surface (.tv-body) before falling back to document.
        const scroller =
          findScrollableAncestor(document.activeElement) ||
          document.querySelector('[data-testid="tv-body"], .tv-body') ||
          document.scrollingElement;
        if (scroller) {
          const dy = (effectiveKey === "PageDown" || effectiveKey === "MediaChannelDown" || effectiveKey === "ChannelDown") ? 1 : -1;
          scroller.scrollBy({ top: dy * scroller.clientHeight * 0.9, behavior: "smooth" });
        }
        return;
      }

      e.preventDefault();
      const focusables = Array.from(document.querySelectorAll('[data-tv-focus="true"]:not([disabled])'))
        .filter((el) => el.offsetParent !== null); // visible only
      if (focusables.length === 0) return;

      const current = focusables.includes(active) ? active : focusables[0];
      const curRect = current.getBoundingClientRect();
      const cx = curRect.left + curRect.width / 2;
      const cy = curRect.top + curRect.height / 2;

      let best = null;
      let bestScore = Infinity;
      focusables.forEach((el) => {
        if (el === current) return;
        const r = el.getBoundingClientRect();
        const x = r.left + r.width / 2;
        const y = r.top + r.height / 2;
        const dx = x - cx;
        const dy = y - cy;

        // Only consider candidates in the pressed direction
        const inDir =
          (effectiveKey === "ArrowUp" && dy < -4) ||
          (effectiveKey === "ArrowDown" && dy > 4) ||
          (effectiveKey === "ArrowLeft" && dx < -4) ||
          (effectiveKey === "ArrowRight" && dx > 4);
        if (!inDir) return;

        // Score: axis-aligned distance weighted (main axis heavy, cross axis penalized)
        const main = effectiveKey === "ArrowUp" || effectiveKey === "ArrowDown" ? Math.abs(dy) : Math.abs(dx);
        const cross = effectiveKey === "ArrowUp" || effectiveKey === "ArrowDown" ? Math.abs(dx) : Math.abs(dy);
        const score = main + cross * 2;
        if (score < bestScore) { bestScore = score; best = el; }
      });

      if (best) {
        best.focus({ preventScroll: false });
        best.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      } else if (effectiveKey === "ArrowDown" || effectiveKey === "ArrowUp") {
        // No focusable candidate in that direction — but user clearly wants
        // to keep surfing. Scroll the nearest scrollable ancestor by a page
        // height so arrow keys always advance, Netflix-style.
        const scroller = findScrollableAncestor(current) || document.scrollingElement;
        if (scroller) {
          const dy = effectiveKey === "ArrowDown" ? 1 : -1;
          scroller.scrollBy({ top: dy * Math.max(240, scroller.clientHeight * 0.6), behavior: "smooth" });
        }
      }
    };

    window.addEventListener("keydown", onKey);

    // ─── Bluetooth / Gamepad support ───────────────────────────────────
    // Polls the Gamepad API ~60Hz. D-pad (buttons 12–15) and left stick
    // (axes 0,1) translate to arrow-key events, A (0) = Enter, B (1) = Back,
    // Start (9) = Back. Works with any HID Bluetooth controller the browser
    // exposes (Xbox, PS5, Switch Pro, 8BitDo, generic gamepads).
    let raf = 0;
    const lastPressed = { up: false, down: false, left: false, right: false, a: false, b: false, start: false };
    const STICK_DEAD = 0.45;
    const fireKey = (key) => {
      const ev = new KeyboardEvent("keydown", { key, bubbles: true, cancelable: true });
      window.dispatchEvent(ev);
    };
    const pollGamepads = () => {
      try {
        const pads = (navigator.getGamepads ? navigator.getGamepads() : []) || [];
        for (const gp of pads) {
          if (!gp) continue;
          const b = gp.buttons || [];
          const a = gp.axes || [];
          const up    = !!b[12]?.pressed || (a[1] ?? 0) < -STICK_DEAD;
          const down  = !!b[13]?.pressed || (a[1] ?? 0) >  STICK_DEAD;
          const left  = !!b[14]?.pressed || (a[0] ?? 0) < -STICK_DEAD;
          const right = !!b[15]?.pressed || (a[0] ?? 0) >  STICK_DEAD;
          const aBtn  = !!b[0]?.pressed;
          const bBtn  = !!b[1]?.pressed;
          const start = !!b[9]?.pressed;
          if (up && !lastPressed.up)       fireKey("ArrowUp");
          if (down && !lastPressed.down)   fireKey("ArrowDown");
          if (left && !lastPressed.left)   fireKey("ArrowLeft");
          if (right && !lastPressed.right) fireKey("ArrowRight");
          if (aBtn && !lastPressed.a) {
            const el = document.activeElement;
            if (el && typeof el.click === "function") el.click();
          }
          if ((bBtn && !lastPressed.b) || (start && !lastPressed.start)) fireKey("Escape");
          lastPressed.up = up; lastPressed.down = down; lastPressed.left = left; lastPressed.right = right;
          lastPressed.a = aBtn; lastPressed.b = bBtn; lastPressed.start = start;
        }
      } catch {}
      raf = requestAnimationFrame(pollGamepads);
    };
    const onGamepadConnected = (e) => {
      try { console.log("[tapin] gamepad connected:", e.gamepad?.id); } catch {}
      if (!raf) raf = requestAnimationFrame(pollGamepads);
    };
    window.addEventListener("gamepadconnected", onGamepadConnected);
    // Kick off polling eagerly — some browsers only expose gamepads once polled
    if (!raf) raf = requestAnimationFrame(pollGamepads);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("gamepadconnected", onGamepadConnected);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [onBack]);
}

// Walk up the DOM to find the first ancestor that can actually scroll
// vertically. Used as a fallback so arrow keys keep surfing even when the
// spatial-nav focus graph runs out of candidates in that direction.
function findScrollableAncestor(node) {
  let el = node?.parentElement;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * useWakeLock — prevents idle screensavers during video playback.
 * Falls back silently on TVs that don't support the Wake Lock API.
 */
export function useWakeLock(active) {
  const lockRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    async function request() {
      try {
        if (!("wakeLock" in navigator)) return;
        if (!active) return;
        // eslint-disable-next-line no-undef
        lockRef.current = await navigator.wakeLock.request("screen");
        lockRef.current.addEventListener?.("release", () => { lockRef.current = null; });
      } catch {}
    }
    async function release() {
      try { await lockRef.current?.release?.(); } catch {}
      lockRef.current = null;
    }
    if (active) request();
    else release();
    const onVis = () => {
      if (active && document.visibilityState === "visible" && !lockRef.current) request();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (!cancelled) release();
      cancelled = true;
    };
  }, [active]);
}
